/**
 * Input sanitization utilities to prevent XSS attacks
 */

/**
 * Sanitizes HTML content by escaping dangerous characters
 * @param input - The input string to sanitize
 * @returns Sanitized string safe for HTML display
 */
export const sanitizeHtml = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Sanitizes text content for safe display
 * @param input - The input string to sanitize
 * @returns Sanitized string safe for text display
 */
export const sanitizeText = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Remove any potential script tags and dangerous content
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');
};

/**
 * Sanitizes search queries to prevent injection attacks
 * @param query - The search query to sanitize
 * @returns Sanitized search query
 */
export const sanitizeSearchQuery = (query: string): string => {
  if (typeof query !== 'string') {
    return '';
  }
  
  // Remove dangerous characters and limit length
  return sanitizeText(query)
    .replace(/[<>'"&]/g, '')
    .substring(0, 100); // Limit length
};

/**
 * Sanitizes track names and other user content
 * @param input - The input string to sanitize
 * @returns Sanitized string safe for display
 */
export const sanitizeUserContent = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Basic sanitization for user content
  return sanitizeText(input)
    .replace(/[<>]/g, '') // Remove angle brackets
    .substring(0, 200); // Reasonable length limit
};

/**
 * Validates and sanitizes email addresses
 * @param email - The email to validate and sanitize
 * @returns Sanitized email or empty string if invalid
 */
export const sanitizeEmail = (email: string): string => {
  if (typeof email !== 'string') {
    return '';
  }
  
  // Basic email validation and sanitization
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sanitized = email.trim().toLowerCase();
  
  if (emailRegex.test(sanitized) && sanitized.length <= 254) {
    return sanitized;
  }
  
  return '';
};

/**
 * Sanitizes URLs to prevent javascript: and other dangerous protocols
 * @param url - The URL to sanitize
 * @returns Sanitized URL or empty string if dangerous
 */
export const sanitizeUrl = (url: string): string => {
  if (typeof url !== 'string') {
    return '';
  }
  
  const trimmed = url.trim();
  
  // Only allow http, https, and mailto protocols
  const allowedProtocols = /^(https?|mailto):/i;
  
  if (allowedProtocols.test(trimmed)) {
    return trimmed;
  }
  
  // If no protocol, assume https
  if (trimmed.startsWith('www.') || trimmed.includes('.')) {
    return `https://${trimmed}`;
  }
  
  return '';
};